/**
 * Wallet provider registry + payload normalization (multi-wallet support, Phase 2).
 *
 * Backend responses carry signing payloads in a few historical shapes (camelCase
 * {uuid, qrUrl, ...} transforms, raw Xaman {uuid, refs, next} payloads, snake_case
 * auth responses). toSigningPayload() funnels them all into the one SigningPayload
 * shape the signing UI consumes. See docs/specs/MULTI_WALLET_SUPPORT.md.
 */

import type { SigningPayload, WalletProviderId, WalletProviderInfo } from '../../types/signing';

export const WALLET_PROVIDERS: Record<WalletProviderId, WalletProviderInfo> = {
  xaman: {
    id: 'xaman',
    name: 'Xaman',
    capabilities: { push: true, qr: true, deepLink: true, clientSigning: false },
  },
  'joey-wc': {
    id: 'joey-wc',
    name: 'Joey',
    // Connect-time pairing uses a QR, but per-transaction signing rides the
    // established WalletConnect session (approve on the phone, no QR per tx).
    capabilities: { push: false, qr: false, deepLink: false, clientSigning: true },
  },
};

export function getWalletProvider(id: WalletProviderId): WalletProviderInfo {
  return WALLET_PROVIDERS[id];
}

/** Branding for the connect UI (badge accent + initial + one-line descriptor). */
export interface WalletBrand {
  name: string;
  /** Accent hex used for the badge + "Open in {name}" CTA. */
  accent: string;
  /** Single-letter mark shown in the badge when no logo asset exists. */
  initial: string;
  /** One-line descriptor shown under the name in the picker. */
  tagline: string;
  /** Show a BETA pill — newly added wallet, may have rough edges. */
  beta?: boolean;
}

export const WALLET_BRANDS: Record<WalletProviderId, WalletBrand> = {
  xaman: { name: 'Xaman', accent: '#3052FF', initial: 'X', tagline: 'Scan a QR or get a push notification' },
  'joey-wc': { name: 'Joey', accent: '#14B8A6', initial: 'J', tagline: 'Connect over WalletConnect', beta: true },
};

/** The provider the signing UI uses when the backend response doesn't say. */
export const DEFAULT_WALLET_PROVIDER: WalletProviderId = 'xaman';

/**
 * Normalize any backend signing-payload shape into SigningPayload.
 *
 * Accepts:
 * - legacy camelCase transforms: { uuid, qrUrl, deepLink, websocketUrl? }
 * - raw Xaman payloads:          { uuid, refs: { qr_png, websocket_status }, next: { always } }
 * - snake_case auth responses:   { uuid, qr_code, deep_link }
 * - future SigningIntent:        { intentId, provider, txJson, xaman? }
 */
export function toSigningPayload(
  raw: Record<string, any> | null | undefined,
  provider: WalletProviderId = DEFAULT_WALLET_PROVIDER
): SigningPayload {
  if (!raw) {
    throw new Error('toSigningPayload: no signing payload provided (expected signingIntent or payload)');
  }
  // Future-proofing: backend SigningIntent shape (Phase 4 routes)
  if (raw.intentId) {
    const xaman = raw.xaman;
    return {
      provider: (raw.provider as WalletProviderId) ?? provider,
      id: raw.intentId,
      qrUrl: xaman?.refs?.qr_png,
      deepLink: xaman?.next?.always,
      websocketUrl: xaman?.refs?.websocket_status,
      txJson: raw.txJson,
    };
  }

  // Raw Xaman payload (uuid + refs)
  if (raw.refs) {
    return {
      provider,
      id: raw.uuid,
      qrUrl: raw.refs.qr_png,
      deepLink: raw.next?.always,
      websocketUrl: raw.refs.websocket_status,
    };
  }

  // Flat shapes (camelCase route transforms / snake_case auth responses)
  return {
    provider,
    id: raw.uuid ?? raw.id,
    qrUrl: raw.qrUrl ?? raw.qr_code ?? raw.qrCode,
    deepLink: raw.deepLink ?? raw.deep_link,
    websocketUrl: raw.websocketUrl ?? raw.websocket_url,
    txJson: raw.txJson,
  };
}
